import { apiClient } from './api';
import { useAuthStore } from '@/store/authStore';

export interface EnrollSecretResponse {
  enroll_secret: string;
  enrollSecret: string;
  organizationName?: string;
  organizationId?: string;
}

export interface OrganizationListItem {
  id: string;
  name: string;
  slug?: string | null;
  status: string;
  sector?: string | null;
  description?: string | null;
  suspendedUntil?: string | null;
  suspendReason?: string | null;
  memberCount?: number;
  createdAt?: string;
  approvedAt?: string | null;
  approvedByUser?: { name?: string; email?: string | null } | null;
  contactFirst?: string | null;
  contactLast?: string | null;
  contactEmail?: string | null;
  source: 'org' | 'request';
  sourceId: string;
}

export interface PaginatedOrganizationsResponse {
  data: OrganizationListItem[];
  total: number;
  page: number;
  pageSize: number;
  /** Counts by status across ALL organizations (not just the current page). */
  statusCounts: Record<string, number>;
}

export interface DeleteOrganizationsResponse {
  requestedCount: number;
  deletedCount: number;
  deletedOrganizationIds: string[];
  notFoundOrganizationIds: string[];
  failedOrganizationIds: string[];
  failedIdentityUserIds: string[];
}

function normalizeStatus(status?: string | null): string {
  return (status ?? 'pending').toLowerCase();
}

function makeSyntheticOrgName(organizationId?: string): string {
  if (!organizationId) {
    return 'Organization';
  }
  return `Organization ${organizationId.slice(0, 8)}`;
}

function parseCompositeId(compositeId: string): { source: 'org' | 'request'; sourceId: string } {
  const [prefix, ...rest] = compositeId.split(':');
  if ((prefix === 'org' || prefix === 'request') && rest.length > 0) {
    return { source: prefix, sourceId: rest.join(':') };
  }
  return { source: 'org', sourceId: compositeId };
}

export const organizationsService = {
  // Lightweight fetch of an org's display branding (name + logo) by raw UUID.
  // Used by the header chip; the /ums/orgs/:id endpoint is open to any
  // authenticated user.
  async getBranding(id: string): Promise<{ name: string; logo: string | null }> {
    const response = await apiClient.get<{ name?: string; logo?: string | null }>(
      `/ums/orgs/${id}`,
    );
    return {
      name: String(response?.name ?? 'Organization'),
      logo: response?.logo ?? null,
    };
  },

  async listAll(params?: { page?: number; pageSize?: number; search?: string; status?: string; sector?: string }): Promise<PaginatedOrganizationsResponse> {
    const response = await apiClient.get<PaginatedOrganizationsResponse | any[]>('/ums/orgs', {
      params: params?.page || params?.pageSize || params?.search || params?.status || params?.sector ? {
        page: params?.page,
        pageSize: params?.pageSize,
        includeRequests: true,
        search: params?.search,
        status: params?.status,
        sector: params?.sector,
      } : undefined,
    });

    const normalizeRow = (row: any): OrganizationListItem => ({
      id: row.id?.startsWith?.('org:') || row.id?.startsWith?.('request:')
        ? String(row.id)
        : `${row.source === 'request' ? 'request' : 'org'}:${row.sourceId ?? row.id}`,
      source: row.source === 'request' ? 'request' : 'org',
      sourceId: String(row.sourceId ?? row.id ?? ''),
      name: String(row.name ?? 'Organization'),
      slug: row.slug ?? null,
      status: normalizeStatus(row.status),
      sector: row.sector ?? null,
      description: row.description ?? null,
      suspendedUntil: row.suspendedUntil ?? null,
      suspendReason: row.suspendReason ?? null,
      memberCount: row.memberCount ?? 0,
      createdAt: row.createdAt ?? null,
      approvedAt: row.approvedAt ?? null,
      approvedByUser: row.approvedByUser ?? null,
      contactFirst: row.contactFirst ?? null,
      contactLast: row.contactLast ?? null,
      contactEmail: row.contactEmail ?? null,
    });

    const countByStatus = (rows: OrganizationListItem[]): Record<string, number> =>
      rows.reduce<Record<string, number>>((acc, row) => {
        acc[row.status] = (acc[row.status] ?? 0) + 1;
        return acc;
      }, {});

    if (Array.isArray(response)) {
      const data = response.map((row) => normalizeRow(row));

      return {
        data,
        total: data.length,
        page: 1,
        pageSize: data.length,
        statusCounts: countByStatus(data),
      };
    }

    const rawRows = Array.isArray(response?.data) ? response.data : [];
    const data = rawRows.map((row) => normalizeRow(row));
    const serverStatusCounts =
      response?.statusCounts && typeof response.statusCounts === 'object'
        ? (response.statusCounts as Record<string, number>)
        : null;

    return {
      data,
      total: Number(response?.total ?? rawRows.length),
      page: Number(response?.page ?? 1),
      pageSize: Number(response?.pageSize ?? rawRows.length),
      // Prefer the server-aggregated counts (covers all organizations); fall
      // back to counting the current page only if the backend hasn't deployed
      // statusCounts yet, so this degrades gracefully rather than breaking.
      statusCounts: serverStatusCounts ?? countByStatus(data),
    };
  },

  async list(): Promise<OrganizationListItem[]> {
    const response = await this.listAll();
    return response.data ?? [];
  },

  async getById(id: string): Promise<any> {
    const parsed = parseCompositeId(id);
    if (parsed.source !== 'request') {
      try {
        const direct = await apiClient.get<any>(`/ums/orgs/${parsed.sourceId}`);
        if (direct && (direct.id || direct.name)) {
          return {
            ...direct,
            source: 'org',
            sourceId: direct.id,
          };
        }
      } catch {
        // Fall back to list search
      }
    }
    const all = await this.listAll();
    return all.data.find((row) => row.id === id || row.sourceId === parsed.sourceId) ?? null;
  },

  async get(id: string) {
    return this.getById(id);
  },

  async getMyEnrollSecret(): Promise<EnrollSecretResponse> {
    const [secretResponse, accessStatus] = await Promise.all([
      apiClient.get<any>('/asset/assets/enroll-secret'),
      apiClient.get<any>('/ums/auth/access-status').catch(() => null),
    ]);

    const enrollSecret = String(secretResponse.enroll_secret ?? '');
    const fallbackOrganizationId = useAuthStore.getState().user?.organizationId;
    const organizationId = String(
      accessStatus?.organization?.id ?? fallbackOrganizationId ?? '',
    );
    const organizationName =
      accessStatus?.organization?.name ?? makeSyntheticOrgName(organizationId);

    return {
      enroll_secret: enrollSecret,
      enrollSecret,
      organizationName,
      organizationId,
    };
  },

  async rotateSecret(id: string): Promise<{ organizationId: string; enroll_secret: string; enrollSecret: string }> {
    const parsed = parseCompositeId(id);
    if (parsed.source !== 'org') {
      throw new Error('Rotate secret is only available for active organizations');
    }

    const response = await apiClient.post<any>(`/ums/orgs/${parsed.sourceId}/enroll-secret/rotate`);
    return {
      organizationId: String(response.organizationId ?? parsed.sourceId),
      enroll_secret: String(response.enroll_secret ?? ''),
      enrollSecret: String(response.enrollSecret ?? response.enroll_secret ?? ''),
    };
  },

  async rotateEnrollSecret(): Promise<EnrollSecretResponse> {
    throw new Error('Use rotateSecret with a specific organization');
  },

  async approve(id: string) {
    const parsed = parseCompositeId(id);
    if (parsed.source !== 'request') {
      throw new Error('Approve is only available for pending registration requests');
    }

    await apiClient.post(`/ums/auth/registration-requests/${parsed.sourceId}/approve`, {});
    return { id, status: 'approved' };
  },

  async reject(id: string) {
    const parsed = parseCompositeId(id);
    if (parsed.source !== 'request') {
      throw new Error('Reject is only available for pending registration requests');
    }

    await apiClient.patch(`/ums/auth/registration-requests/${parsed.sourceId}/reject`, {
      reason: 'Rejected by platform administrator',
    });
    return { id, status: 'rejected' };
  },

  async suspend(id: string, options?: { suspendedUntil?: string; reason?: string }) {
    const parsed = parseCompositeId(id);
    if (parsed.source !== 'org') {
      throw new Error('Suspend is only available for active organizations');
    }

    await apiClient.patch(`/ums/orgs/${parsed.sourceId}/suspend`, options ?? {});
    return { id, status: 'suspended' };
  },

  async reactivate(id: string) {
    const parsed = parseCompositeId(id);
    if (parsed.source !== 'org') {
      throw new Error('Reactivate is only available for organizations');
    }

    await apiClient.patch(`/ums/orgs/${parsed.sourceId}/reactivate`, {});
    return { id, status: 'active' };
  },

  async setInactive(id: string) {
    const parsed = parseCompositeId(id);
    if (parsed.source !== 'org') {
      throw new Error('Set inactive is only available for organizations');
    }

    await apiClient.patch(`/ums/orgs/${parsed.sourceId}/inactive`, {});
    return { id, status: 'inactive' };
  },

  async deleteMany(ids: string[]): Promise<DeleteOrganizationsResponse> {
    const parsedIds = ids.map((id) => parseCompositeId(id));
    const nonOrgSelected = parsedIds.some((item) => item.source !== 'org');
    if (nonOrgSelected) {
      throw new Error('Delete is only available for organizations');
    }

    const organizationIds = Array.from(new Set(parsedIds.map((item) => item.sourceId)));
    if (organizationIds.length === 0) {
      throw new Error('No organizations selected for deletion');
    }

    return apiClient.delete<DeleteOrganizationsResponse>('/ums/orgs', {
      data: { organizationIds },
    });
  },

  async update(id: string, data: any) {
    // `id` may be a composite (`org:<uuid>` / `request:<uuid>`); the orgs PATCH
    // endpoint expects the raw organization id.
    const { sourceId } = parseCompositeId(id);
    return apiClient.patch(`/ums/orgs/${sourceId}`, data);
  },
};
